import { useEffect, useRef, useState } from "react";
import AssistantService from "../../../services/AssistantService";
import {
  Assistant,
  CasuistryAction
} from "../../../entities/company/Assistant/Assistant";
import SegmentService from "../../../services/SegmentService";
import { SegmentListResponse } from "../../../entities/segment/SegmentListResponse";
import { TranslationService } from "../../../services/TranslationService";
import { ToastService } from "../../shared/bootstrap/Toast";
import CompanyService from "../../../services/CompanyService";
import Switch from "react-switch";
import IARuleService, { DefinitionRule } from "../../../services/IARuleService";
import UsecasesService from "../../../services/UsecasesService";
import { NumberInput } from "../../shared/components/Editors";

/* ===== Tipos locales ===== */

type Segment = { id: number; name: string };

type ConditionDraft = {
  typeRule: 0 | 1 | 2;
  definitionValues: number[];
  confidence: number | null;
};

type LocalRule = {
  iaRuleID: number | null;
  conditions: ConditionDraft[];
};

/* ===== Constantes ===== */

const CASUISTRY_ACTIONS: { value: number; label: string }[] = [
  { value: 1, label: "Responder el correo" },
  { value: 2, label: "Actualizar el estado de facturas" },
  { value: 3, label: "Crear tarea interna" },
  { value: 4, label: "Enviar listado de facturas en email" },
  { value: 5, label: "Enviar link al portal de clientes en email" }
];

const RULE_TYPES: { value: 0 | 1 | 2; label: string }[] = [
  { value: 0, label: "Segmento" },
  { value: 1, label: "Casuística detectada" },
  { value: 2, label: "Confianza mínima" }
];

const emptyCondition = (): ConditionDraft => ({
  typeRule: 0,
  definitionValues: [],
  confidence: null
});

const toDefinitionRule = (c: ConditionDraft): DefinitionRule => ({
  typeRule: c.typeRule,
  ...(c.typeRule !== 2 ? { definitionValues: c.definitionValues } : {}),
  ...(c.typeRule === 2 ? { confidence: c.confidence } : {})
});

/* ===== MultiCheckDropdown ===== */

const MultiCheckDropdown = ({
  options,
  value,
  onChange,
  placeholder = "Seleccionar...",
  disabled
}: {
  options: { value: number; label: string }[];
  value: number[];
  onChange: (values: number[]) => void;
  placeholder?: string;
  disabled?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) {
        setOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open) {
      setTimeout(() => searchRef.current?.focus(), 0);
    } else {
      setSearchTerm("");
    }
  }, [open]);

  const toggle = (v: number) =>
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);

  const selected = options.filter((o) => value.includes(o.value));
  const filtered = searchTerm.trim()
    ? options.filter((o) =>
        o.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  return (
    <div className="position-relative" ref={ref}>
      <div
        className="d-flex align-items-center flex-wrap gap-1"
        style={{
          border: "1px solid #dee2e6",
          borderRadius: 4,
          padding: "3px 28px 3px 6px",
          minHeight: 34,
          cursor: disabled ? "default" : "pointer",
          background: disabled ? "#e9ecef" : "#fff",
          position: "relative"
        }}
        onClick={() => !disabled && setOpen((o) => !o)}
      >
        {selected.length === 0 ? (
          <span style={{ color: "#bbb", fontSize: 12, padding: "1px 2px" }}>
            {placeholder}
          </span>
        ) : (
          selected.map((opt) => (
            <span
              key={opt.value}
              className="d-inline-flex align-items-center gap-1"
              style={{
                background: "rgba(0,118,182,0.08)",
                color: "#0076b6",
                borderRadius: 3,
                padding: "1px 6px",
                fontSize: 11,
                lineHeight: "18px"
              }}
            >
              {opt.label}
              <i
                className="fas fa-times"
                style={{ fontSize: 9, cursor: "pointer" }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  toggle(opt.value);
                }}
              />
            </span>
          ))
        )}
        <i
          className="fas fa-chevron-down"
          style={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: 10,
            color: "#aaa",
            pointerEvents: "none"
          }}
        />
      </div>

      {open && (
        <div
          className="dropdown-menu show shadow-sm"
          style={{
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 2000,
            padding: "4px 0"
          }}
        >
          <div className="px-2 pb-1 pt-1">
            <input
              ref={searchRef}
              type="text"
              className="form-control form-control-sm"
              style={{ fontSize: 12 }}
              placeholder="Buscar segmento..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div style={{ maxHeight: 200, overflowY: "auto" }}>
            {filtered.length === 0 ? (
              <span
                className="d-block px-3 py-1 text-muted"
                style={{ fontSize: 12 }}
              >
                Sin resultados
              </span>
            ) : (
              filtered.map((opt) => (
                <label
                  key={opt.value}
                  className="d-flex align-items-center gap-2 px-3 py-1 mb-0 w-100"
                  style={{
                    cursor: "pointer",
                    fontSize: 12,
                    fontWeight: "normal"
                  }}
                >
                  <input
                    type="checkbox"
                    className="form-check-input mt-0 flex-shrink-0"
                    checked={value.includes(opt.value)}
                    onChange={() => toggle(opt.value)}
                  />
                  {opt.label}
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ===== RuleCard ===== */

type RuleCardProps = {
  rule: LocalRule;
  ruleIndex: number;
  segments: Segment[];
  onSave: (index: number, conditions: ConditionDraft[]) => Promise<void>;
  onDelete: (index: number) => Promise<void>;
  deletable?: boolean;
};

const RuleCard = ({
  rule,
  ruleIndex,
  segments,
  onSave,
  onDelete,
  deletable = true
}: RuleCardProps) => {
  const [conditions, setConditions] = useState<ConditionDraft[]>(
    rule.conditions.length > 0 ? rule.conditions : [emptyCondition()]
  );
  const [saving, setSaving] = useState(false);
  const [valueInputs, setValueInputs] = useState<Record<number, string>>({});

  const updateCondition = (i: number, upd: Partial<ConditionDraft>) =>
    setConditions((prev) =>
      prev.map((c, idx) => (idx === i ? { ...c, ...upd } : c))
    );

  const addCasuistryCode = (i: number) => {
    const num = parseInt(valueInputs[i] ?? "", 10);
    if (isNaN(num)) return;
    if (!conditions[i].definitionValues.includes(num)) {
      updateCondition(i, {
        definitionValues: [...conditions[i].definitionValues, num]
      });
    }
    setValueInputs((prev) => ({ ...prev, [i]: "" }));
  };

  const removeCasuistryCode = (i: number, v: number) =>
    updateCondition(i, {
      definitionValues: conditions[i].definitionValues.filter((x) => x !== v)
    });

  const canSave = conditions.every((c) => {
    if (c.typeRule === 0 || c.typeRule === 1)
      return c.definitionValues.length > 0;
    if (c.typeRule === 2) return c.confidence !== null;
    return false;
  });

  const handleSave = async () => {
    setSaving(true);
    await onSave(ruleIndex, conditions);
    setSaving(false);
  };

  const handleDelete = async () => {
    setSaving(true);
    await onDelete(ruleIndex);
    setSaving(false);
  };

  return (
    <div
      className="border rounded"
      style={{ background: "#fafafa", borderColor: "#e0e0e0" }}
    >
      <div
        className="d-flex align-items-center justify-content-between px-3 py-2 rounded-top"
        style={{ background: "#f3f3f3", borderBottom: "0.5px solid #e8e8e8" }}
      >
        <small className="fw-medium text-secondary d-flex align-items-center gap-2">
          <i className="fas fa-code-branch" style={{ fontSize: 12 }} />
          Regla {ruleIndex + 1}
        </small>
        {deletable && (
          <button
            className="btn btn-sm text-muted p-1"
            onClick={handleDelete}
            disabled={saving}
            style={{ lineHeight: 1, fontSize: 14 }}
          >
            <i className="far fa-trash-alt" />
          </button>
        )}
      </div>

      <div className="p-3">
        {conditions.map((cond, i) => (
          <div key={i}>
            {i > 0 && (
              <div className="mb-2">
                <span
                  className="text-muted"
                  style={{
                    background: "#ebebeb",
                    borderRadius: 10,
                    padding: "2px 9px",
                    fontSize: 11,
                    display: "inline-block"
                  }}
                >
                  Y además
                </span>
              </div>
            )}

            <div className="d-flex align-items-start gap-2 mb-2 flex-wrap">
              <select
                className="form-select form-select-sm"
                style={{ width: 180, flexShrink: 0, fontSize: 12, height: 32 }}
                value={cond.typeRule}
                onChange={(e) => {
                  const t = parseInt(e.target.value) as 0 | 1 | 2;
                  updateCondition(i, {
                    typeRule: t,
                    definitionValues: [],
                    confidence: null
                  });
                  setValueInputs((prev) => ({ ...prev, [i]: "" }));
                }}
                disabled={saving}
              >
                {RULE_TYPES.map((rt) => (
                  <option key={rt.value} value={rt.value}>
                    {rt.label}
                  </option>
                ))}
              </select>

              <span
                className="text-muted align-self-center"
                style={{ fontSize: 12, flexShrink: 0 }}
              >
                {cond.typeRule === 2 ? "es" : "incluye"}
              </span>

              <div className="flex-grow-1">
                {cond.typeRule === 0 && (
                  <MultiCheckDropdown
                    options={segments.map((s) => ({
                      value: s.id,
                      label: s.name
                    }))}
                    value={cond.definitionValues}
                    onChange={(vals) =>
                      updateCondition(i, { definitionValues: vals })
                    }
                    placeholder="Seleccionar segmentos..."
                    disabled={saving}
                  />
                )}

                {cond.typeRule === 1 && (
                  <div className="d-flex flex-wrap align-items-center gap-1">
                    <NumberInput
                      min={1}
                      className="form-control form-control-sm"
                      style={{ width: 90, fontSize: 12 }}
                      placeholder="Código"
                      value={valueInputs[i] ?? ""}
                      onChange={(e) =>
                        setValueInputs((prev) => ({
                          ...prev,
                          [i]: e.target.value
                        }))
                      }
                      onKeyDown={(e) =>
                        e.key === "Enter" && addCasuistryCode(i)
                      }
                      disabled={saving}
                    />
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      disabled={!(valueInputs[i] ?? "") || saving}
                      onClick={() => addCasuistryCode(i)}
                    >
                      +
                    </button>
                    {cond.definitionValues.map((v) => (
                      <span
                        key={v}
                        className="d-inline-flex align-items-center gap-1"
                        style={{
                          background: "#e8f2fb",
                          color: "#0076b6",
                          borderRadius: 4,
                          padding: "2px 6px",
                          fontSize: 11
                        }}
                      >
                        {String(v).padStart(2, "0")}
                        <i
                          className="fas fa-times pointer"
                          style={{ fontSize: 9 }}
                          onClick={() => removeCasuistryCode(i, v)}
                        />
                      </span>
                    ))}
                  </div>
                )}

                {cond.typeRule === 2 && (
                  <div className="d-flex align-items-center gap-1">
                    <span className="small text-muted">≥</span>
                    <NumberInput
                      min={0}
                      max={100}
                      className="form-control form-control-sm"
                      style={{ width: 80, fontSize: 12 }}
                      placeholder="0–100"
                      value={cond.confidence ?? ""}
                      onChange={(e) =>
                        updateCondition(i, {
                          confidence:
                            e.target.value === ""
                              ? null
                              : parseInt(e.target.value)
                        })
                      }
                      disabled={saving}
                    />
                    <span className="small text-muted">%</span>
                  </div>
                )}
              </div>

              <button
                className="btn btn-sm text-muted p-1 align-self-center"
                style={{ lineHeight: 1, fontSize: 13 }}
                onClick={() =>
                  setConditions((prev) => prev.filter((_, idx) => idx !== i))
                }
                disabled={saving || conditions.length <= 1}
              >
                <i className="far fa-times" />
              </button>
            </div>
          </div>
        ))}

        <div className="d-flex align-items-center justify-content-between mt-2">
          <button
            className="btn btn-sm d-flex align-items-center gap-1"
            style={{
              border: "0.5px dashed #ccc",
              borderRadius: 6,
              color: "#999",
              fontSize: 12,
              padding: "5px 12px",
              background: "transparent"
            }}
            onClick={() => setConditions((prev) => [...prev, emptyCondition()])}
            disabled={saving}
          >
            <i className="far fa-plus" style={{ fontSize: 11 }} />
            Agregar condición
          </button>
          <button
            className="btn btn-sm btn-primary"
            onClick={handleSave}
            disabled={saving || !canSave}
            style={{ fontSize: 12 }}
          >
            {saving ? (
              <i className="fas fa-spinner-third fa-spin" />
            ) : (
              "Guardar regla"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ===== AssistantConfiguration ===== */

const AssistantConfiguration = () => {
  const [assistant, setAssistant] = useState<Assistant | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [isAutonomous, setAutonomous] = useState(false);
  const [instructions, setInstructions] = useState("");
  const [segments, setSegments] = useState<Segment[]>([]);
  const [localRules, setLocalRules] = useState<LocalRule[]>([]);
  const [casuistryActions, setCasuistryActions] = useState<CasuistryAction[]>(
    []
  );
  const [newCasuistryCode, setNewCasuistryCode] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [resultAssistant, segs, rules, usecases] = await Promise.all([
          AssistantService.getInstructions(),
          SegmentService.getList().then((res) => {
            if (res instanceof Error) return [];
            return (res as SegmentListResponse).list
              .flatMap((folder) => folder.items)
              .map((s) => ({ id: s.SegmentID, name: s.Name }));
          }),
          IARuleService.list(),
          UsecasesService.get()
        ]);

        if (!(resultAssistant instanceof Error) && resultAssistant) {
          setAssistant(resultAssistant);
          setInstructions(resultAssistant.instructions ?? "");
          setEnabled(Boolean(resultAssistant.enabled));
          setAutonomous(resultAssistant.isAutonomous);
        }

        if (!(usecases instanceof Error)) {
          setCasuistryActions(usecases);
        }

        if (!(segs instanceof Error) && Array.isArray(segs)) {
          setSegments(segs);
        }

        if (!(rules instanceof Error)) {
          setLocalRules(
            rules.map((r) => ({
              iaRuleID: r.iaRuleID,
              conditions: r.definitionRule.map((c) => ({
                typeRule: c.typeRule,
                definitionValues: c.definitionValues ?? [],
                confidence: c.confidence ?? null
              }))
            }))
          );
        }
      } catch (e: any) {
        ToastService.showToast(
          e?.message ?? "Error al cargar la configuración.",
          undefined,
          "danger"
        );
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const buildPayload = (): Assistant => ({
    id: assistant?.id ?? "",
    instructions,
    enabled,
    isAutonomous
  });

  const handleSave = async () => {
    const invalidCasuistry = casuistryActions.some(
      (ca) => ca.actions.length === 0
    );
    if (invalidCasuistry) {
      ToastService.showToast(
        "Cada casuística debe tener al menos una acción seleccionada.",
        undefined,
        "danger"
      );
      return;
    }
    setSaving(true);
    try {
      const [assistantResult, usecasesResult] = await Promise.all([
        AssistantService.setInstructions(buildPayload()),
        UsecasesService.set(casuistryActions)
      ]);
      if (assistantResult instanceof Error) throw assistantResult;
      if (usecasesResult instanceof Error) throw usecasesResult;
      ToastService.showToast(
        TranslationService.translate?.SavedSuccessfully ??
          "¡Guardado exitosamente!",
        undefined,
        "success"
      );
    } catch (e: any) {
      ToastService.showToast(
        e?.message ?? "No se pudo guardar la configuración.",
        undefined,
        "danger"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSavePhantomRule = async (conditions: ConditionDraft[]) => {
    const result = await IARuleService.save({
      iaRuleID: null,
      definitionRule: conditions.map(toDefinitionRule)
    });
    if (result instanceof Error) {
      ToastService.showToast(
        result.message ?? "Error al guardar la regla.",
        undefined,
        "danger"
      );
      return;
    }
    const freshRules = await IARuleService.list();
    if (!(freshRules instanceof Error)) {
      setLocalRules(
        freshRules.map((r) => ({
          iaRuleID: r.iaRuleID,
          conditions: r.definitionRule.map((c) => ({
            typeRule: c.typeRule,
            definitionValues: c.definitionValues ?? [],
            confidence: c.confidence ?? null
          }))
        }))
      );
    }
    ToastService.showToast(
      TranslationService.translate?.SavedSuccessfully ?? "Regla guardada.",
      undefined,
      "success"
    );
  };

  const handleSaveRule = async (
    index: number,
    conditions: ConditionDraft[]
  ) => {
    const rule = localRules[index];
    const unsavedRules = localRules.filter(
      (r, i) => i !== index && r.iaRuleID === null
    );
    const result = await IARuleService.save({
      iaRuleID: rule.iaRuleID,
      definitionRule: conditions.map(toDefinitionRule)
    });
    if (result instanceof Error) {
      ToastService.showToast(
        result.message ?? "Error al guardar la regla.",
        undefined,
        "danger"
      );
      return;
    }
    const freshRules = await IARuleService.list();
    if (!(freshRules instanceof Error)) {
      setLocalRules([
        ...freshRules.map((r) => ({
          iaRuleID: r.iaRuleID,
          conditions: r.definitionRule.map((c) => ({
            typeRule: c.typeRule,
            definitionValues: c.definitionValues ?? [],
            confidence: c.confidence ?? null
          }))
        })),
        ...unsavedRules
      ]);
    } else {
      setLocalRules((prev) =>
        prev.map((r, i) =>
          i === index
            ? {
                iaRuleID: (result as { iaRuleId: number }).iaRuleId,
                conditions
              }
            : r
        )
      );
    }
    ToastService.showToast(
      TranslationService.translate?.SavedSuccessfully ?? "Regla guardada.",
      undefined,
      "success"
    );
  };

  const handleDeleteRule = async (index: number) => {
    const rule = localRules[index];
    if (rule.iaRuleID !== null) {
      const result = await IARuleService.delete(rule.iaRuleID);
      if (result instanceof Error) {
        ToastService.showToast(
          result.message ?? "Error al eliminar la regla.",
          undefined,
          "danger"
        );
        return;
      }
    }
    setLocalRules((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddCasuistryRow = () => {
    const code = parseInt(newCasuistryCode, 10);
    if (isNaN(code) || code < 1) return;
    if (casuistryActions.some((ca) => ca.code === code)) return;
    setCasuistryActions((prev) => [...prev, { code, actions: [] }]);
    setNewCasuistryCode("");
  };

  const handleRemoveCasuistryRow = (code: number) =>
    setCasuistryActions((prev) => prev.filter((ca) => ca.code !== code));

  const handleSetCasuistryActions = (code: number, actions: number[]) =>
    setCasuistryActions((prev) =>
      prev.map((ca) => (ca.code !== code ? ca : { ...ca, actions }))
    );

  const advancedUser =
    CompanyService.isBackOfficeUser() || CompanyService.inHouse();

  return (
    <div className="main-layout-container mx-3">
      <div className="main-panel">
        <h4 className="mb-4">
          {TranslationService.translate?.AssistantConfigurationTitle ??
            "Configuración del Agente de categorización de emails"}
        </h4>

        <div className="card">
          {loading ? (
            <div>
              <i className="fas fa-spinner-third fa-spin third ms-2" />
            </div>
          ) : (
            <>
              {advancedUser && (
                <>
                  {/* Modo autónomo */}
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div className="fw-semibold">
                        {TranslationService.translate?.EnableAutonomousMode ??
                          "Habilitar modo autónomo"}
                      </div>
                    </div>
                    <Switch
                      onColor="#0076b6"
                      checked={isAutonomous}
                      onChange={(val) => setAutonomous(val)}
                      height={24}
                      width={50}
                      uncheckedIcon={false}
                      checkedIcon={false}
                      disabled={saving}
                    />
                  </div>

                  {/* Reglas de autonomía */}
                  {isAutonomous && (
                    <>
                      <hr className="my-3" />
                      <div>
                        <div className="fw-medium mb-1">
                          Reglas de autonomía
                        </div>
                        <p className="text-muted small mb-3">
                          El agente actúa autónomamente si{" "}
                          <strong>al menos una regla</strong> se cumple. Dentro
                          de cada regla, <strong>todas las condiciones</strong>{" "}
                          deben cumplirse.
                        </p>

                        {localRules.length === 0 ? (
                          <RuleCard
                            rule={{
                              iaRuleID: null,
                              conditions: [emptyCondition()]
                            }}
                            ruleIndex={0}
                            segments={segments}
                            deletable={false}
                            onSave={async (_, conditions) =>
                              handleSavePhantomRule(conditions)
                            }
                            onDelete={async () => {}}
                          />
                        ) : (
                          localRules.map((rule, i) => (
                            <div key={rule.iaRuleID ?? `new-${i}`}>
                              {i > 0 && (
                                <div className="d-flex align-items-center gap-2 my-3">
                                  <div
                                    className="flex-grow-1"
                                    style={{
                                      height: 1,
                                      background: "#e0e0e0"
                                    }}
                                  />
                                  <span
                                    className="text-muted bg-white border"
                                    style={{
                                      borderRadius: 10,
                                      padding: "2px 10px",
                                      fontSize: 11
                                    }}
                                  >
                                    O
                                  </span>
                                  <div
                                    className="flex-grow-1"
                                    style={{
                                      height: 1,
                                      background: "#e0e0e0"
                                    }}
                                  />
                                </div>
                              )}
                              <RuleCard
                                rule={rule}
                                ruleIndex={i}
                                segments={segments}
                                onSave={handleSaveRule}
                                onDelete={handleDeleteRule}
                              />
                            </div>
                          ))
                        )}

                        <button
                          className="btn btn-sm d-flex align-items-center gap-1 mt-2"
                          style={{
                            border: "0.5px solid #0076b6",
                            borderRadius: 6,
                            color: "#0076b6",
                            fontSize: 13,
                            padding: "7px 16px"
                          }}
                          onClick={() =>
                            setLocalRules((prev) =>
                              prev.length === 0
                                ? [
                                    {
                                      iaRuleID: null,
                                      conditions: [emptyCondition()]
                                    },
                                    {
                                      iaRuleID: null,
                                      conditions: [emptyCondition()]
                                    }
                                  ]
                                : [
                                    ...prev,
                                    {
                                      iaRuleID: null,
                                      conditions: [emptyCondition()]
                                    }
                                  ]
                            )
                          }
                          disabled={saving}
                        >
                          <i className="far fa-plus" />
                          Agregar regla
                        </button>

                        <div
                          className="d-flex align-items-center gap-2 p-3 mt-3 rounded"
                          style={{
                            background: "#e8f2fb",
                            border: "0.5px solid #b5cef4"
                          }}
                        >
                          <i
                            className="far fa-info-circle"
                            style={{
                              color: "#0076b6",
                              fontSize: 15,
                              flexShrink: 0
                            }}
                          />
                          <small style={{ color: "#0076b6", lineHeight: 1.5 }}>
                            Si ninguna regla se cumple, el mensaje es procesado
                            en <strong>modo Copiloto</strong> y el gestor recibe
                            las recomendaciones para revisarlas antes de
                            ejecutar.
                          </small>
                        </div>
                      </div>
                    </>
                  )}

                  <hr className="my-3" />

                  {/* Acciones por casuística */}
                  <div>
                    <div className="fw-semibold mb-1">
                      {TranslationService.translate?.CasuistryActions ??
                        "Acciones por casuística"}
                    </div>
                    <p className="text-muted small mb-3">
                      Definí qué acciones debe ejecutar el agente cuando detecta
                      cada casuística.
                    </p>

                    {casuistryActions.length === 0 ? (
                      <div
                        className="text-center py-4 text-muted mb-3"
                        style={{
                          border: "0.5px dashed #ddd",
                          borderRadius: 8,
                          background: "#fafafa",
                          fontSize: 12
                        }}
                      >
                        Sin casuísticas configuradas. Agregá una usando el campo
                        de abajo.
                      </div>
                    ) : (
                      <table className="table table-sm mb-2">
                        <thead>
                          <tr>
                            <th
                              style={{
                                width: 90,
                                fontSize: 11.5,
                                color: "#888",
                                fontWeight: 500
                              }}
                            >
                              Casuística
                            </th>
                            <th
                              style={{
                                fontSize: 11.5,
                                color: "#888",
                                fontWeight: 500
                              }}
                            >
                              Acciones
                            </th>
                            <th style={{ width: 40 }} />
                          </tr>
                        </thead>
                        <tbody>
                          {[...casuistryActions]
                            .sort((a, b) => a.code - b.code)
                            .map((ca) => (
                              <tr key={ca.code}>
                                <td className="align-middle">
                                  <span
                                    className="fw-semibold"
                                    style={{
                                      fontFamily: "monospace",
                                      fontSize: 11,
                                      background: "#e8f2fb",
                                      color: "#0076b6",
                                      borderRadius: 4,
                                      padding: "2px 6px"
                                    }}
                                  >
                                    {String(ca.code).padStart(2, "0")}
                                  </span>
                                </td>
                                <td>
                                  <MultiCheckDropdown
                                    options={CASUISTRY_ACTIONS}
                                    value={ca.actions}
                                    onChange={(actions) =>
                                      handleSetCasuistryActions(
                                        ca.code,
                                        actions
                                      )
                                    }
                                    disabled={saving}
                                  />
                                </td>
                                <td className="align-middle text-center">
                                  <i
                                    className="far fa-trash-alt text-muted pointer"
                                    style={{ fontSize: 14 }}
                                    onClick={() =>
                                      handleRemoveCasuistryRow(ca.code)
                                    }
                                  />
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    )}

                    <div className="d-flex align-items-center gap-2">
                      <NumberInput
                        min={1}
                        className="form-control form-control-sm"
                        style={{ maxWidth: 340, fontSize: 12 }}
                        placeholder="Código de casuística"
                        value={newCasuistryCode}
                        onChange={(e) => setNewCasuistryCode(e.target.value)}
                        onKeyDown={(e) =>
                          e.key === "Enter" && handleAddCasuistryRow()
                        }
                        disabled={saving}
                      />
                      <button
                        className="btn btn-sm d-flex align-items-center gap-1"
                        style={{
                          border: "0.5px solid #0076b6",
                          borderRadius: 6,
                          color: "#0076b6",
                          fontSize: 12,
                          padding: "6px 14px"
                        }}
                        disabled={!newCasuistryCode || saving}
                        onClick={handleAddCasuistryRow}
                      >
                        <i className="far fa-plus" style={{ fontSize: 11 }} />
                        Agregar
                      </button>
                    </div>
                  </div>

                  <hr className="my-3" />

                  {/* Instrucciones (advanced) */}
                  <div>
                    <div className="fw-semibold mb-1">
                      {TranslationService.translate?.Instructions ??
                        "Instrucciones"}
                    </div>
                    <textarea
                      className="form-control"
                      style={{
                        width: "100%",
                        minHeight: 150,
                        fontSize: 13,
                        lineHeight: 1.6,
                        resize: "vertical"
                      }}
                      value={instructions}
                      onChange={(e) => setInstructions(e.target.value)}
                      disabled={saving}
                      placeholder={
                        "Rol y objetivo\n\nActuá como asistente de cobranzas de Intiza..."
                      }
                    />
                  </div>
                </>
              )}

              {/* Instrucciones (non-advanced) */}
              {!advancedUser && (
                <div>
                  <div className="fw-semibold mb-1">
                    {TranslationService.translate?.Instructions ??
                      "Instrucciones"}
                  </div>
                  <textarea
                    className="form-control"
                    style={{
                      width: "100%",
                      minHeight: 150,
                      fontSize: 13,
                      lineHeight: 1.6,
                      resize: "vertical"
                    }}
                    value={instructions}
                    onChange={(e) => setInstructions(e.target.value)}
                    disabled={saving}
                    placeholder={
                      "Rol y objetivo\n\nActuá como asistente de cobranzas de Intiza..."
                    }
                  />
                </div>
              )}
            </>
          )}
        </div>

        {!loading && (
          <div className="d-flex align-items-center gap-2 mt-3">
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
              style={{ padding: "9px 28px", fontSize: 14 }}
            >
              {TranslationService.translate?.Save ?? "Guardar"}
            </button>
            {saving && (
              <i className="fas fa-spinner-third fa-spin third ms-2" />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AssistantConfiguration;
